import React, { Fragment } from 'react';
import { Formik } from 'formik';
import { Grid } from '@material-ui/core';
import _ from 'lodash'
import styled from 'styled-components';
import { compose } from 'redux';
import { connect } from "react-redux";
import { MoneyInput, TextInput, DatePicker, AutoCompleteInput, IntegerInput } from 'components/Global/FormikMaterial';
import {
  PARALLEL_SHOCK,
  LIBOR_FORWARD_CURVE,
  MARKET_EXPECTATIONS_SHOCK,
  TREASURIES
} from "../Constants/Constants";
import { FIXED_RATE } from 'components/Calculators/Constants/InterestRateType';
import {
  calculatePrepaymentPenalty,
  downloadPrepaymentPenaltyTestData,
  getYieldTestData
} from "store/state/calculators/testHarness";
import {
  dayCountTypes, shocks, aggregateInterestMethods
} from "../Constants/DropDownLists";
import { PrimaryButton, ButtonSizeOptions, SecondaryButton } from '@loanbossinc/component-library';
import moment from 'moment';
import AmortizationType, { MORTGAGE_STYLE, STRAIGHT_LINE_CUSTOM_AMOUNT } from '../Constants/AmortizationType';
import InterestRatesFormSection from './InterestRatesFormSection';
import AmortizationFormSection from './AmortizationFormSection';
import PrepaymentFormSection from './PrepaymentFormSection';
import FormSectionTitle from './FormSectionTitle';
import RateEnvironmentSection from './RateEnvironmentSection';
import ImmediateResults from './ImmediateResults';

const TopParentGrid = styled(Grid)`  
  padding-left:15px;
  max-width:670px;
`;
const ParentGrid = styled(Grid)`
  padding-top:40px;
  padding-left:15px;
  max-width:670px;
`;
const LastGrid = styled(Grid)`
  padding-left:15px;
`;
const ButtonBar = styled(Grid)`
  && > *:first-child {
    margin-right: 12px;
  }
`;
const StyledAutoCompleteInput = styled(AutoCompleteInput)`
  & {
    width:100%;
  }
`;
const StyledMoneyInput = styled(MoneyInput)`
  & {
    width:100%;
  }
`;

const StyledIntegerInput = styled(IntegerInput)`
  & {
    width:100%;
  }
`;
const StyledDatePicker = styled(DatePicker)`
  & {
    width:100%;
  }
`;
const StyledLabel = styled.div`{
  //styleName: Work Sans / 16 Medium;
  font-family: Work Sans;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
  letter-spacing: 0em;
  text-align: left;
 }`;
class PrepaymentForm extends React.Component {
  state = {
    periodsRemainingUntilOpen: 0
  }

  componentDidUpdate(prevProps) {
    const { periodsRemainingUntilOpen } = this.state;
    this.props.getYieldTestData({ curve: TREASURIES, tenorValue: periodsRemainingUntilOpen / 12 });
  }

  transformRate = (val) => {
    return +val / 100;
  }

  createAmortizationParameters = (values) => {
    const {
      amortizationRate,
      amortizationTerm,
      amortizationType,
      baseDate,
      customMonthlyPrincipalReduction,
      dayCount,
      interestOnlyPeriod,
      interestRate,
      loanAmount,
      loanTerm,
      payment,
      shockType,
      shockValue,
      spread,
      startDate,
    } = values;

    const amortizationParameters = {
      amortizationTerm,
      baseDate,
      customMonthlyPrincipalReduction,
      dayCount,
      interestOnlyPeriod,
      loanAmount: +loanAmount,
      loanTerm,
      payment,
      shockType,
      shockValue,
      startDate,
    };
    if (interestRate !== null)
      amortizationParameters.interestRate = this.transformRate(interestRate);
    else
      delete amortizationParameters.interestRate;

    if (amortizationType !== STRAIGHT_LINE_CUSTOM_AMOUNT)
      delete amortizationParameters.customMonthlyPrincipalReduction;

    if (spread)
      amortizationParameters.spread = spread / 10000; // bp == 1/100 * 0.01

    if (amortizationRate && amortizationType === MORTGAGE_STYLE)
      amortizationParameters.amortizationRate = this.transformRate(amortizationRate);
    else if (!amortizationRate && amortizationType === MORTGAGE_STYLE)
      amortizationParameters.amortizationRate = this.transformRate(interestRate);
    else
      delete amortizationParameters.amortizationRate;

    if (!amortizationParameters.shockType)
      amortizationParameters.shockType = MARKET_EXPECTATIONS_SHOCK;

    if (amortizationParameters.shockType === PARALLEL_SHOCK && amortizationParameters.shockValue && amortizationParameters.shockValue !== 0)
      amortizationParameters.shockValue *= 0.0001;
    else if (amortizationParameters.shockType === MARKET_EXPECTATIONS_SHOCK)
      delete amortizationParameters.shockValue;

    if (!amortizationParameters.interestOnlyPeriod)
      amortizationParameters.interestOnlyPeriod = 0;

    amortizationParameters.baseDate = moment(amortizationParameters.baseDate).format("YYYY-MM-DD");
    amortizationParameters.startDate = moment(amortizationParameters.startDate).format("YYYY-MM-DD");
    return amortizationParameters;
  }

  createPrepaymentParameters = (values) => {
    const prepaymentParameters = {};
    prepaymentParameters.prepaymentType = values.prepaymentType;
    prepaymentParameters.flatPercentage = this.transformRate(values.flatPercentage);
    prepaymentParameters.prepaymentDate = moment(values.prepaymentDate).format("YYYY-MM-DD");
    prepaymentParameters.minimumPenalty = this.transformRate(values.minimumPenalty);
    prepaymentParameters.interestRate = this.transformRate(values.interestRate);
    prepaymentParameters.replacementRateSpread = this.transformRate(values.replacementRateSpread);
    prepaymentParameters.openPeriods = values.openPeriods;
    prepaymentParameters.lookbackDays = values.lookbackDays || 0;
    prepaymentParameters.interestRateRounding = values.interestRateRounding || "NO_ROUNDING";
    prepaymentParameters.shouldUsePV = values.shouldUsePV;

    if (values.prepaymentType === "AGGREGATE_INTEREST") {
      prepaymentParameters.aggregateInterestMethod = values.aggregateInterestMethod;
      if (values.aggregateInterestMethod === "DEFINED") prepaymentParameters.definedAggregateInterestAmount = values.definedAggregateInterestAmount;
      if (values.aggregateInterestMethod === "FORMULAIC") prepaymentParameters.monthsOfInterest = values.monthsOfInterest;
    }

    return prepaymentParameters
  }

  createAmortizationConfig = (values) => {
    return {
      "curve": values.curve,
      "interestRateType": values.interestRateType,
      "amortizationType": values.amortizationType
    }
  }

  handleDownload = (values) => {
    let prepaymentRequest = {};
    prepaymentRequest.amortizationParameters = this.createAmortizationParameters(values);
    prepaymentRequest.prepaymentParameters = this.createPrepaymentParameters(values);
    prepaymentRequest.amortizationConfig = this.createAmortizationConfig(values);
    this.props.downloadPrepaymentPenaltyTestData(prepaymentRequest);
  }

  updatePeriodsRemainingUntilOpen = (value) => {
    this.setState({ periodsRemainingUntilOpen: value });
  }

  render() {
    const { calculatePrepaymentPenalty } = this.props;
    let prepaymentForm = {
      amortizationRate: '',
      amortizationTerm: '',
      amortizationType: '',
      baseDate: '',
      curve: '',
      customMonthlyPrincipalReduction: '',
      dayCount: '',
      interestOnlyPeriod: '',
      interestRateType: '',
      interestRate: '',
      replacementRateSpread: '0.0',
      loanAmount: '',
      loanTerm: '',
      payment: '',
      shockType: MARKET_EXPECTATIONS_SHOCK,
      shockValue: '',
      spread: '',
      startDate: null,
      //prepayment
      prepaymentType: '',
      prepaymentDate: null,
      openPeriods: '',
      minimumPenalty: '',
      aggregateInterestMethod: '',
      definedAggregateInterestAmount: '',
      monthsOfInterest: '',
      shouldUsePV: false,
    }
    return (
      <Formik
        initialValues={prepaymentForm}
        validateOnChange={false}
        validate={values => {
          let errors = {}
          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          let prepaymentRequest = {};
          prepaymentRequest.amortizationParameters = this.createAmortizationParameters(values);
          prepaymentRequest.prepaymentParameters = this.createPrepaymentParameters(values);
          prepaymentRequest.amortizationConfig = this.createAmortizationConfig(values);
          this.props.moveToAnalysisResult(prepaymentRequest);
          calculatePrepaymentPenalty(prepaymentRequest);
          setSubmitting(false);
        }}
        render={({ values, errors, status, touched, isSubmitting, handleChange, handleBlur, handleSubmit }) => (
          <Fragment>
            <TopParentGrid container alignItems="center" spacing={8}>
              <PrepaymentFormSection updatePeriodsRemainingUntilOpen={this.updatePeriodsRemainingUntilOpen} />
            </TopParentGrid>

            <ParentGrid container alignItems="center" justify="flex-start" spacing={8}>
              <Grid item xs={12}>
                <FormSectionTitle title="Loan Information" />
              </Grid>
              <Grid item xs={6}>
                <StyledMoneyInput
                  label={<StyledLabel>{"Loan Amount"}</StyledLabel>}
                  fieldName="loanAmount" />
              </Grid>
              <Grid item xs={6}>
                <StyledDatePicker
                  label={<StyledLabel>{"Closing Date"}</StyledLabel>}
                  fieldName="startDate" />
              </Grid>
              <Grid item xs={6}>
                <StyledIntegerInput
                  label={<StyledLabel>{"Loan Term (m)"}</StyledLabel>}
                  fieldName="loanTerm" />
              </Grid>
              <Grid item xs={6}>
                <StyledAutoCompleteInput
                  label={<StyledLabel>{"Day Count"}</StyledLabel>}

                  fieldName="dayCount"
                  selectValue="value"
                  options={dayCountTypes} />
              </Grid>
            </ParentGrid>

            <ParentGrid container alignItems="center" justify="flex-start" spacing={8}>
              <InterestRatesFormSection />
            </ParentGrid>

            <ParentGrid container alignItems="center" spacing={8}>
              <AmortizationFormSection />
            </ParentGrid>
            <ParentGrid container alignItems="center" spacing={8} hidden={values.interestRateType === FIXED_RATE}>
              <RateEnvironmentSection />
            </ParentGrid>
            <LastGrid container alignItems="center" spacing={8}>
              <ImmediateResults values={values} periodsRemainingUntilOpen={this.state.periodsRemainingUntilOpen} />
            </LastGrid>

            <ParentGrid container alignItems="center" spacing={8}>
              <ButtonBar item xs={12}>
                <PrimaryButton
                  style={{ marginRight: 24 }}
                  label="Calculate"
                  onClick={handleSubmit}
                  size={ButtonSizeOptions.smFluid}
                />
                <SecondaryButton
                  label="Download"
                  onClick={() => this.handleDownload(values)}
                  size={ButtonSizeOptions.smFluid}
                />
              </ButtonBar>
            </ParentGrid>
          </Fragment>
        )} />
    );
  }
}
function mapStateToProps({ testHarness }) {
  return {
    prepaymentPenalty: testHarness.prepaymentPenalty,
    yieldCurveResult: _.get(testHarness, 'testData.yieldCurveResult', 0)
  }
}

export default compose(
  connect(
    mapStateToProps,
    {
      calculatePrepaymentPenalty,
      downloadPrepaymentPenaltyTestData,
      getYieldTestData
    }
  ))(PrepaymentForm);
